/**
 * Soft blocked / incomplete workspace shells — always include escape paths.
 * Never leave the operator on a dead-end card with no Home / Settings.
 * Always wraps a usable workflow strip + AI innov (soft copy only).
 */

import { escapeHtml } from "./dom.js";
import { responseAiInnovHtml, responseLoopWorkbenchHtml } from "./response-loop-workbench.js";

/**
 * @param {{
 *   title?: string,
 *   detail?: string,
 *   eyebrow?: string,
 *   backHref?: string,
 *   backLabel?: string,
 *   innovKind?: "before"|"during"|"field"|"after"|"notify",
 *   loopActiveId?: string,
 *   loopTitle?: string,
 *   loopLede?: string,
 *   badge?: string,
 *   extraActions?: Array<{ href: string, label: string, ghost?: boolean }>
 * }} opts
 */
export function workspaceBlockedHtml(opts = {}) {
  const title = escapeHtml(String(opts.title || "Not available yet"));
  const detail = escapeHtml(
    String(
      opts.detail
        || "This desk is not ready for your account yet. Use Home or Settings to continue.",
    ),
  );
  const eyebrow = escapeHtml(String(opts.eyebrow || "Unavailable"));
  const badge = escapeHtml(String(opts.badge || "Not open yet"));
  const backLabel = escapeHtml(String(opts.backLabel || "Back to Settings"));
  const innovKind = String(opts.innovKind || "during");
  const loopActiveId = String(opts.loopActiveId || "notice");
  const loopTitle = String(opts.loopTitle || "Desk not open yet");
  const loopLede = String(
    opts.loopLede
      || "This surface is closed for your role — Home still runs tonight's loop.",
  );
  const extras = Array.isArray(opts.extraActions) ? opts.extraActions : [];
  const destOf = (raw) => String(raw || "overview").replace(/^#\/?/, "");
  const extraHtml = extras
    .map((a) => {
      const dest = escapeHtml(destOf(a.href));
      const label = escapeHtml(String(a.label || "Open"));
      const cls = a.ghost ? "btn ghost btn-tap" : "btn secondary btn-tap";
      return `<button type="button" class="${cls}" data-cc-go="${dest}">${label}</button>`;
    })
    .join("");
  const backDest = destOf(opts.backHref || "settings");
  return `<div class="croc-desk-stack croc-desk-stack--blocked">
  ${responseLoopWorkbenchHtml({
    compact: true,
    activeId: loopActiveId,
    title: loopTitle,
    lede: loopLede,
  })}
  <div class="capability-unavailable-shell workspace-blocked-shell">
    <div class="capability-unavailable-main">
      <section class="card glass-panel glass-panel--panel capability-unavailable-card">
        <div class="incidents-wb-strip glass-panel glass-panel--inset" role="region" aria-label="Closed desk shortcuts">
          <div>
            <p class="incidents-wb-strip__title">${title}</p>
            <p class="muted" style="margin:4px 0 0;font-size:13px;line-height:1.4">Use Home and Assignments for the living loop · Settings for account tools.</p>
          </div>
          <div class="incidents-wb-strip__actions">
            <button type="button" class="btn sm btn-tap" data-cc-go="overview">Home</button>
            <button type="button" class="btn sm secondary btn-tap" data-cc-go="response">Assignments</button>
            <button type="button" class="btn sm secondary btn-tap" data-cc-go="incidents">Cases</button>
            <button type="button" class="btn sm ghost btn-tap" data-cc-go="settings">Settings</button>
          </div>
        </div>
        <p class="incident-workspace__eyebrow">${eyebrow}</p>
        <h2 class="ui-section-title" style="margin:0 0 8px">${title}</h2>
        <p class="badge offline">${badge}</p>
        <p class="muted capability-unavailable-card__copy">${detail}</p>
        <div class="capability-unavailable-card__actions row" style="gap:8px;flex-wrap:wrap;margin-top:16px">
          <button type="button" class="btn btn-tap" data-cc-go="overview">Home</button>
          <button type="button" class="btn secondary btn-tap" data-cc-go="response">Assignments</button>
          <button type="button" class="btn ghost btn-tap" data-cc-go="${escapeHtml(backDest)}">${backLabel}</button>
          ${extraHtml}
        </div>
      </section>
    </div>
    <aside class="incident-list-rail glass-panel glass-panel--panel" aria-label="Next steps">
      <p class="incident-workspace__eyebrow">Next</p>
      <h3 class="incident-list-rail__title">Keep working</h3>
      <p class="muted incident-list-rail__copy">This page is not open for you right now. Jump back to Home, Settings, or Groups — those desks stay available.</p>
      <nav class="incident-list-rail__actions" aria-label="Related workspaces">
        <button type="button" class="btn secondary btn-tap" data-cc-go="overview">Home</button>
        <button type="button" class="btn secondary btn-tap" data-cc-go="response">Assignments</button>
        <button type="button" class="btn secondary btn-tap" data-cc-go="incidents">Cases</button>
        <button type="button" class="btn ghost btn-tap" data-cc-go="settings">Settings</button>
      </nav>
    </aside>
  </div>
  ${responseAiInnovHtml(innovKind)}
</div>`;
}
